import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const PackagePerformanceAnalytics = ({ packages }) => {
  const analytics = useMemo(() => {
    const totalPackages = packages?.length || 0;
    const activePackages = packages?.filter(p => p?.status === 'active')?.length || 0;
    const totalRevenue = packages?.reduce((sum, p) => sum + (parseFloat(p?.revenueGenerated) || 0), 0);
    const totalSales = packages?.reduce((sum, p) => sum + (parseInt(p?.totalSales) || 0), 0);
    const averagePrice = totalPackages > 0 
      ? packages?.reduce((sum, p) => sum + (parseFloat(p?.finalPrice) || 0), 0) / totalPackages 
      : 0;

    const packagesByTier = {
      bronze: packages?.filter(p => p?.tier === 'bronze')?.length || 0,
      silver: packages?.filter(p => p?.tier === 'silver')?.length || 0,
      gold: packages?.filter(p => p?.tier === 'gold')?.length || 0
    };

    const topPerformers = packages
      ?.sort((a, b) => (b?.revenueGenerated || 0) - (a?.revenueGenerated || 0))
      ?.slice(0, 5);

    return {
      totalPackages,
      activePackages,
      totalRevenue,
      totalSales,
      averagePrice,
      packagesByTier,
      topPerformers
    };
  }, [packages]);

  const formatAmount = (value) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2
    })?.format(value || 0);
  };

  const getTierIcon = (tier) => {
    const icons = {
      bronze: 'Medal',
      silver: 'Award',
      gold: 'Crown'
    };
    return icons?.[tier] || 'Package';
  };

  const getTierColor = (tier) => {
    const colors = {
      bronze: 'text-orange-600 bg-orange-50',
      silver: 'text-gray-600 bg-gray-50',
      gold: 'text-yellow-600 bg-yellow-50'
    };
    return colors?.[tier] || 'text-gray-600 bg-gray-50';
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg border border-border shadow-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="Package" size={24} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Packages</p>
              <p className="text-2xl font-bold text-foreground">{analytics?.totalPackages}</p>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {analytics?.activePackages} actifs
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border shadow-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-success/10 rounded-lg">
              <Icon name="DollarSign" size={24} className="text-success" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Revenus Totaux</p>
              <p className="text-2xl font-bold text-foreground">
                {formatAmount(analytics?.totalRevenue)}
              </p>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            {analytics?.totalSales} ventes
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border shadow-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-warning/10 rounded-lg">
              <Icon name="TrendingUp" size={24} className="text-warning" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Prix Moyen</p>
              <p className="text-2xl font-bold text-foreground">
                {formatAmount(analytics?.averagePrice)}
              </p>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Par package
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border shadow-card p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Icon name="ShoppingCart" size={24} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Ventes Totales</p>
              <p className="text-2xl font-bold text-foreground">{analytics?.totalSales}</p>
            </div>
          </div>
          <div className="text-xs text-muted-foreground">
            Toutes catégories
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-card rounded-lg border border-border shadow-card p-6">
          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <Icon name="BarChart3" size={20} className="text-primary" />
            Répartition par Tier
          </h3>
          <div className="space-y-4">
            {Object.entries(analytics?.packagesByTier)?.map(([tier, count]) => (
              <div key={tier}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Icon name={getTierIcon(tier)} size={18} className={getTierColor(tier)?.split(' ')?.[0]} />
                    <span className="font-medium text-foreground capitalize">{tier}</span>
                  </div>
                  <span className="font-semibold text-foreground">{count}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div
                    className={`h-2 rounded-full ${getTierColor(tier)?.split(' ')?.[1]}`}
                    style={{ width: `${analytics?.totalPackages > 0 ? (count / analytics?.totalPackages) * 100 : 0}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border shadow-card p-6">
          <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
            <Icon name="Trophy" size={20} className="text-primary" />
            Top 5 Packages
          </h3>
          <div className="space-y-3">
            {analytics?.topPerformers?.length > 0 ? (
              analytics?.topPerformers?.map((pkg, index) => (
                <div
                  key={pkg?.id}
                  className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                    index === 0 ? 'bg-yellow-100 text-yellow-700' :
                    index === 1 ? 'bg-gray-100 text-gray-700' :
                    index === 2 ? 'bg-orange-100 text-orange-700': 'bg-muted text-muted-foreground'
                  }`}>
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-foreground">{pkg?.packageName}</h4>
                    <p className="text-xs text-muted-foreground">{pkg?.packageCode}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-success">{formatAmount(pkg?.revenueGenerated)}</p>
                    <p className="text-xs text-muted-foreground">{pkg?.totalSales} ventes</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <Icon name="Package" size={48} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Aucune donnée disponible</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-card rounded-lg border border-border shadow-card p-6">
        <h3 className="text-lg font-bold text-foreground mb-4 flex items-center gap-2">
          <Icon name="List" size={20} className="text-primary" />
          Tous les Packages
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Package</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Tier</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Prix</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Services</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Ventes</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Revenus</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-foreground">Statut</th>
              </tr>
            </thead>
            <tbody>
              {packages?.map(pkg => (
                <tr key={pkg?.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                  <td className="py-3 px-4">
                    <div>
                      <p className="font-medium text-foreground">{pkg?.packageName}</p>
                      <p className="text-xs text-muted-foreground">{pkg?.packageCode}</p>
                    </div>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getTierColor(pkg?.tier)}`}>
                      <Icon name={getTierIcon(pkg?.tier)} size={12} />
                      {pkg?.tier}
                    </span>
                  </td>
                  <td className="py-3 px-4 font-semibold text-foreground">
                    {formatAmount(pkg?.finalPrice)}
                  </td>
                  <td className="py-3 px-4 text-muted-foreground">
                    {pkg?.services?.length || 0}
                  </td>
                  <td className="py-3 px-4 text-foreground">
                    {pkg?.totalSales || 0}
                  </td>
                  <td className="py-3 px-4 font-semibold text-success">
                    {formatAmount(pkg?.revenueGenerated)}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      pkg?.status === 'active' ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'
                    }`}>
                      {pkg?.status === 'active' ? 'Actif' : pkg?.status === 'inactive' ? 'Inactif' : 'Archivé'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PackagePerformanceAnalytics;